import { useEffect, useMemo, useState } from 'react';

export const useResizableDrawer = (props, ref) => {
  const { open, setOpen } = props;
  const [height, setHeight] = useState(props.height || 'fit-content');
  const [viewHeight, setViewHeight] = useState(window.innerHeight);
  const [isResizing, setIsResizing] = useState(false);
  const breakpoints = useMemo(() => [viewHeight * 0.5, viewHeight * 0.95], [viewHeight]);
  const [actuallyBreakpoint, setActuallyBreakpoint] = useState(0);

  useEffect(() => {
    if (typeof height == 'number' && height <= 0.2 * viewHeight) {
      setOpen(false);
    }
  }, [height, viewHeight, setOpen]);

  useEffect(() => {
    if (!open) {
      setIsResizing(false);
    }
  }, [open]);

  useEffect(() => {
    const resizeListener = () => {
      setViewHeight(window.innerHeight);
    };

    // Calcular el valor al montar el componente
    resizeListener();

    // Actualizar el valor cuando la ventana cambie de tamaño
    window.addEventListener('resize', resizeListener);

    // Limpiar el event listener cuando el componente se desmonte
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [open]);

  useEffect(() => {
    if (ref.current) {
      if (props.height) {
        setHeight(props.height);
      } else if (ref.current.offsetHeight <= viewHeight * 0.4) {
        setHeight(breakpoints[0]);
        setActuallyBreakpoint(0);
      } else {
        setHeight(breakpoints[1]);
        setActuallyBreakpoint(1);
      }
    }
  }, [props.height, viewHeight, ref, open, breakpoints]);

  const handleMousedown = () => {
    setIsResizing(true);
  };

  const handleMouseup = () => {
    setIsResizing(false);
  };

  const handleMousemove = (e: MouseEvent | TouchEvent) => {
    if (!isResizing) {
      return;
    }

    const clientY = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
    const offsetTop = document.body.offsetHeight - (clientY - document.body.offsetTop);
    const offsetHeight = height - offsetTop;

    if (Math.abs(offsetHeight) < viewHeight * 0.1) {
      return;
    } else if (props.height) {
      if (offsetHeight > 0) {
        setOpen(false);
      }
    } else if (offsetHeight > 0) {
      if (actuallyBreakpoint == 0) {
        setOpen(false);
      } else {
        setIsResizing(false);
        setHeight(breakpoints[actuallyBreakpoint - 1]);
        setActuallyBreakpoint(actuallyBreakpoint - 1);
      }
    } else if (offsetHeight < 0) {
      if (actuallyBreakpoint == breakpoints.length - 1) {
        return;
      } else {
        setIsResizing(false);
        setHeight(breakpoints[actuallyBreakpoint + 1]);
        setActuallyBreakpoint(actuallyBreakpoint + 1);
      }
    }
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMousemove);
      document.addEventListener('touchmove', handleMousemove);
    } else {
      document.removeEventListener('mousemove', handleMousemove);
      document.removeEventListener('touchmove', handleMousemove);
    }

    return () => {
      document.removeEventListener('mousemove', handleMousemove);
      document.removeEventListener('touchmove', handleMousemove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseup);
    document.addEventListener('touchend', handleMouseup);

    return () => {
      document.removeEventListener('mouseup', handleMouseup);
      document.removeEventListener('touchend', handleMouseup);
    };
  }, []);

  return { handleMousedown, height };
};
